import { useState, useEffect } from 'react';
import { Download, Upload, Plus, Edit2, Trash2 } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { MetalPiece } from '../types';
import Modal from './Modal';
import LoadingState from './LoadingState';
import { IndeterminateProgressBar } from './ProgressBar';
import ErrorMessage from './ErrorMessage';
import { EmptyStateNoData } from './EmptyState';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import FormField from './FormField';
import { pieceCodeValidator, nameValidator, weightValidator, costValidator } from '../utils/validators';

export default function MetalPieces() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [metalPieces, setMetalPieces] = useState<MetalPiece[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPieceType, setSelectedPieceType] = useState<string>(() => {
    const saved = localStorage.getItem('metal_pieces_filter_type');
    if (saved) {
      try {
        return saved;
      } catch (e) {
        return 'ALL';
      }
    }
    return 'ALL';
  });
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>(() => {
    const saved = localStorage.getItem('metal_pieces_filter_active');
    if (saved) {
      try {
        return saved;
      } catch (e) {
        return 'ALL';
      }
    }
    return 'ALL';
  });

  // Función helper para determinar el tipo de pieza basándose en el código
  const getPieceType = (codigo: string): 'ANILLO' | 'COLGANTE' | 'PULSERA' | 'REASA' | null => {
    if (!codigo || typeof codigo !== 'string') return null;
    
    const cleaned = codigo.trim().toUpperCase();
    if (cleaned.length === 0) return null;
    
    const firstChar = cleaned.charAt(0);
    
    if (firstChar === 'A') return 'ANILLO';
    if (firstChar === 'C') return 'COLGANTE';
    if (firstChar === 'P') return 'PULSERA';
    if (firstChar === 'R') return 'REASA';
    
    return null;
  };

  const selectPieceType = (type: string) => {
    setSelectedPieceType(type);
    localStorage.setItem('metal_pieces_filter_type', type);
  };

  const selectActiveStatus = (status: string) => {
    setActiveStatusFilter(status);
    localStorage.setItem('metal_pieces_filter_active', status);
  };

  // Filtrar piezas según búsqueda, tipo de pieza y estado activo
  const filteredPieces = metalPieces.filter((piece) => {
    // Filtro por tipo de pieza
    if (selectedPieceType !== 'ALL') {
      const pieceType = getPieceType(piece.cod_pieza);
      if (pieceType !== selectedPieceType) return false;
    }

    // Filtro por estado activo/inactivo
    if (activeStatusFilter !== 'ALL') {
      if (activeStatusFilter === 'ACTIVE' && !piece.is_active) return false;
      if (activeStatusFilter === 'INACTIVE' && piece.is_active) return false;
    }

    // Filtro por búsqueda
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      piece.cod_pieza.toLowerCase().includes(search) ||
      piece.nombre_pieza.toLowerCase().includes(search) ||
      piece.material_base.toLowerCase().includes(search)
    );
  });

  // Paginación
  const {
    currentPage,
    totalPages,
    paginatedItems,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
  } = usePagination(filteredPieces, {
    initialItemsPerPage: 25,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [editingPiece, setEditingPiece] = useState<MetalPiece | null>(null);
  const [pieceType, setPieceType] = useState<'PULSERA' | 'COLGANTE' | 'REASA' | 'ANILLO'>('ANILLO');
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [formData, setFormData] = useState({
    cod_pieza: '',
    nombre_pieza: '',
    material_base: 'PLATA' as 'PLATA' | 'LATON',
    peso_plata: '',
    peso_laton: '',
    coste_fundicion: '',
    coste_pvd: '',
    coste_dlc: '',
    is_active: true,
  });

  useEffect(() => {
    loadMetalPieces();
  }, []);

  const loadMetalPieces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/metal-pieces', {
        params: { includeInactive: 'true' }
      });
      setMetalPieces(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar piezas metálicas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (piece?: MetalPiece) => {
    if (piece) {
      setEditingPiece(piece);
      setFormData({
        cod_pieza: piece.cod_pieza,
        nombre_pieza: piece.nombre_pieza,
        material_base: piece.material_base,
        peso_plata: piece.peso_plata?.toString() || '',
        peso_laton: piece.peso_laton?.toString() || '',
        coste_fundicion: piece.coste_fundicion?.toString() || '',
        coste_pvd: piece.coste_pvd?.toString() || '',
        coste_dlc: piece.coste_dlc?.toString() || '',
        is_active: piece.is_active,
      });

      // Inferir tipo de pieza a partir del código existente (solo para mostrar en el combo)
      const firstChar = piece.cod_pieza?.trim()?.charAt(0).toUpperCase();
      if (firstChar === 'P') setPieceType('PULSERA');
      else if (firstChar === 'C') setPieceType('COLGANTE');
      else if (firstChar === 'R') setPieceType('REASA');
      else setPieceType('ANILLO');
      setSuggestedCode(null);
    } else {
      setEditingPiece(null);
      setFormData({
        cod_pieza: '',
        nombre_pieza: '',
        material_base: 'PLATA',
        peso_plata: '',
        peso_laton: '',
        coste_fundicion: '',
        coste_pvd: '',
        coste_dlc: '',
        is_active: true,
      });
      setPieceType('ANILLO');
      setSuggestedCode(null);
      // Cargar sugerencia inicial
      void fetchSuggestedCode('ANILLO', 'PLATA');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPiece(null);
    setSuggestedCode(null);
  };

  const fetchSuggestedCode = async (
    type: 'PULSERA' | 'COLGANTE' | 'REASA' | 'ANILLO',
    material: 'PLATA' | 'LATON'
  ) => {
    // Solo sugerir cuando se está creando una nueva pieza
    if (editingPiece) return;

    try {
      setLoadingSuggestion(true);
      const response = await api.get('/metal-pieces/next-code', {
        params: { type, material },
      });

      if (response.data?.next_code) {
        setSuggestedCode(response.data.next_code);
        // Solo auto-rellenar si el usuario no ha escrito nada todavía
        if (!formData.cod_pieza) {
          setFormData((prev) => ({
            ...prev,
            cod_pieza: response.data.next_code,
          }));
        }
      }
    } catch (err: any) {
      console.error('Error obteniendo código sugerido:', err);
      showError(err.response?.data?.error || 'No se pudo obtener el siguiente código sugerido');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        peso_plata: formData.peso_plata ? parseFloat(formData.peso_plata) : undefined,
        peso_laton: formData.peso_laton ? parseFloat(formData.peso_laton) : undefined,
        coste_fundicion: formData.coste_fundicion ? parseFloat(formData.coste_fundicion) : undefined,
        coste_pvd: formData.coste_pvd ? parseFloat(formData.coste_pvd) : undefined,
        coste_dlc: formData.coste_dlc ? parseFloat(formData.coste_dlc) : undefined,
      };

      if (editingPiece) {
        await api.put(`/metal-pieces/${editingPiece.id}`, data);
        showSuccess('Pieza metálica actualizada correctamente');
      } else {
        await api.post('/metal-pieces', data);
        showSuccess('Pieza metálica creada correctamente');
      }

      handleCloseModal();
      loadMetalPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar pieza metálica');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta pieza metálica?')) {
      return;
    }

    try {
      await api.delete(`/metal-pieces/${id}`);
      showSuccess('Pieza metálica eliminada correctamente');
      loadMetalPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pieza metálica');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/metal-pieces/export', {
        responseType: 'blob',
      });

      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'piezas_metalicas_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('CSV descargado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar CSV');
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      showError('Por favor selecciona un archivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await api.post('/metal-pieces/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      
      if (response.data.errors && response.data.errors.length > 0) {
        showError(`Importación completada con ${response.data.errors.length} errores`);
      } else {
        showSuccess(`Importación exitosa: ${response.data.created} creadas, ${response.data.updated} actualizadas`);
      }

      // Recargar la lista si hubo éxito
      if (response.data.created > 0 || response.data.updated > 0) {
        loadMetalPieces();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al importar piezas metálicas');
      setImportResult(null);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="flex-between mb-2" style={{ justifyContent: 'flex-end' }}>
        {canEdit('metal-pieces') && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleExportCSV}>
              <Download size={16} /> Descargar CSV
            </button>
            <button className="btn btn-secondary" onClick={() => setIsImportModalOpen(true)}>
              <Upload size={16} /> Importar CSV
            </button>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={16} /> Nueva Pieza
            </button>
          </div>
        )}
      </div>

      {/* Filtros por tipo de pieza */}
      <div className="table-container" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.125rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              FILTRAR POR TIPO DE PIEZA
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'ALL', label: 'Todos', color: '#6b7280' },
                { value: 'ANILLO', label: 'Anillo', color: '#3b82f6' },
                { value: 'COLGANTE', label: 'Colgante', color: '#a855f7' },
                { value: 'PULSERA', label: 'Pulsera', color: '#10b981' },
                { value: 'REASA', label: 'Reasa', color: '#f59e0b' },
              ].map((type) => {
                const isSelected = selectedPieceType === type.value;
                return (
                  <label
                    key={type.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${type.color}15` : 'transparent',
                      color: isSelected ? type.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${type.color}20` : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="pieceTypeFilter"
                      value={type.value}
                      checked={isSelected}
                      onChange={() => selectPieceType(type.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? type.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? type.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}>
                      {isSelected && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }} />
                      )}
                    </div>
                    <span>{type.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.125rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              FILTRAR POR ESTADO
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'ALL', label: 'Todas', color: '#6b7280' },
                { value: 'ACTIVE', label: 'Solo Activas', color: '#10b981' },
                { value: 'INACTIVE', label: 'Solo Inactivas', color: '#ef4444' },
              ].map((status) => {
                const isSelected = activeStatusFilter === status.value;
                return (
                  <label
                    key={status.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? status.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${status.color}15` : 'transparent',
                      color: isSelected ? status.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${status.color}20` : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="activeStatusFilter"
                      value={status.value}
                      checked={isSelected}
                      onChange={() => selectActiveStatus(status.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? status.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? status.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                    }}>
                      {isSelected && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }} />
                      )}
                    </div>
                    <span>{status.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <input
              type="text"
              className="input"
              placeholder="Buscar por código, nombre o material..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: '250px' }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingState type="table" message="Cargando piezas metálicas..." />
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Peso Plata</th>
                  <th>Peso Latón</th>
                  <th>Coste Fundición</th>
                  <th>Coste PVD</th>
                  <th>Coste DLC</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPieces.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center" style={{ padding: '2rem' }}>
                      {searchTerm ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          No se encontraron piezas con ese criterio de búsqueda
                        </div>
                      ) : (
                        <EmptyStateNoData onCreate={canEdit('metal-pieces') ? () => handleOpenModal() : undefined} />
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((piece) => (
                <tr key={piece.id} style={{ padding: '0.375rem 0' }}>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    <strong
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                        color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                      }}
                    >
                      {piece.cod_pieza}
                    </strong>
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.nombre_pieza}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.peso_plata || '-'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.peso_laton || '-'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.coste_fundicion ? `€${piece.coste_fundicion}` : '-'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.coste_pvd ? `€${piece.coste_pvd}` : '-'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{piece.coste_dlc ? `€${piece.coste_dlc}` : '-'}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    <span style={{ color: piece.is_active ? 'var(--success)' : 'var(--text-secondary)' }}>
                      {piece.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>
                    {canEdit('metal-pieces') ? (
                      <div className="flex gap-1">
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleOpenModal(piece)}
                          style={{ padding: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDelete(piece.id)}
                          style={{ padding: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        Sin permisos
                      </span>
                    )}
                  </td>
                </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredPieces.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredPieces.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              showItemsPerPage={true}
            />
          )}
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPiece ? 'Editar Pieza Metálica' : 'Nueva Pieza Metálica'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              data-modal-default="true"
            >
              {editingPiece ? 'Actualizar' : 'Crear'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Tipo de Pieza"
            name="pieceType"
            type="select"
            value={pieceType}
            onChange={(value) => {
              const newType = value as 'PULSERA' | 'COLGANTE' | 'REASA' | 'ANILLO';
              setPieceType(newType);
              fetchSuggestedCode(newType, formData.material_base);
            }}
            disabled={!!editingPiece}
            options={[
              { value: 'PULSERA', label: 'Pulsera (P)' },
              { value: 'COLGANTE', label: 'Colgante (C)' },
              { value: 'REASA', label: 'Reasa (R)' },
              { value: 'ANILLO', label: 'Anillo (A)' },
            ]}
          />

          <FormField
            label="Código de Pieza"
            name="cod_pieza"
            type="text"
            value={formData.cod_pieza}
            onChange={(value) => setFormData({ ...formData, cod_pieza: String(value).toUpperCase() })}
            required
            disabled={!!editingPiece}
            placeholder={suggestedCode || undefined}
            validator={pieceCodeValidator()}
            helpText={!editingPiece && suggestedCode ? `Sugerencia: ${suggestedCode}${loadingSuggestion ? ' (calculando...)' : ''}` : undefined}
          />

          <FormField
            label="Nombre de Pieza"
            name="nombre_pieza"
            type="text"
            value={formData.nombre_pieza}
            onChange={(value) => setFormData({ ...formData, nombre_pieza: String(value) })}
            required
            validator={nameValidator()}
          />

          <FormField
            label="Material Base"
            name="material_base"
            type="select"
            value={formData.material_base}
            onChange={(value) => {
              const newMaterial = value as 'PLATA' | 'LATON';
              const updated = { 
                ...formData, 
                material_base: newMaterial,
                peso_plata: newMaterial === 'PLATA' ? formData.peso_plata : '',
                peso_laton: newMaterial === 'LATON' ? formData.peso_laton : '',
              };
              setFormData(updated);
              fetchSuggestedCode(pieceType, newMaterial);
            }}
            required
            options={[
              { value: 'PLATA', label: 'Plata' },
              { value: 'LATON', label: 'Latón' },
            ]}
          />

          {formData.material_base === 'PLATA' && (
            <FormField
              label="Peso Plata (g)"
              name="peso_plata"
              type="number"
              value={formData.peso_plata || ''}
              onChange={(value) => setFormData({ ...formData, peso_plata: String(value) })}
              step={0.001}
              validator={weightValidator()}
            />
          )}

          {formData.material_base === 'LATON' && (
            <FormField
              label="Peso Latón (g)"
              name="peso_laton"
              type="number"
              value={formData.peso_laton || ''}
              onChange={(value) => setFormData({ ...formData, peso_laton: String(value) })}
              step={0.001}
              validator={weightValidator()}
            />
          )}

          <FormField
            label="Coste Fundición (€)"
            name="coste_fundicion"
            type="number"
            value={formData.coste_fundicion || ''}
            onChange={(value) => setFormData({ ...formData, coste_fundicion: String(value) })}
            step={0.01}
            validator={costValidator()}
          />

          <FormField
            label="Coste PVD (€)"
            name="coste_pvd"
            type="number"
            value={formData.coste_pvd || ''}
            onChange={(value) => setFormData({ ...formData, coste_pvd: String(value) })}
            step={0.01}
            validator={costValidator()}
          />

          <FormField
            label="Coste DLC (€)"
            name="coste_dlc"
            type="number"
            value={formData.coste_dlc || ''}
            onChange={(value) => setFormData({ ...formData, coste_dlc: String(value) })}
            step={0.01}
            validator={costValidator()}
          />

          <div className="input-group">
            <label className="input-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{ marginRight: '0.5rem' }}
              />
              Activa
            </label>
          </div>
        </form>
      </Modal>

      {/* Modal de Importación CSV */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportFile(null);
          setImportResult(null);
        }}
        title="Importar Piezas Metálicas desde CSV"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsImportModalOpen(false);
                setImportFile(null);
                setImportResult(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              data-modal-default="true"
            >
              {importing ? 'Importando...' : 'Importar'}
            </button>
            {importing && (
              <div style={{ marginTop: '1rem' }}>
                <IndeterminateProgressBar
                  label="Importando piezas metálicas..."
                  variant="primary"
                  size="medium"
                />
              </div>
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <p style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
              Selecciona un archivo CSV con las piezas metálicas a importar.
            </p>
            <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              <strong>Formato esperado:</strong> cod_pieza, nombre_pieza, material_base, peso_plata (opcional), peso_laton (opcional), coste_fundicion (opcional), coste_pvd (opcional), coste_dlc (opcional), is_active (opcional)
            </p>
            <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--info)', fontStyle: 'italic' }}>
              💡 <strong>Nota:</strong> Si una pieza ya existe (mismo código), solo se actualizarán los campos que estén presentes en el CSV. Los campos vacíos o ausentes no modificarán los valores existentes.
            </p>
          </div>

          <div className="input-group">
            <label className="input-label">Archivo CSV *</label>
            <input
              type="file"
              accept=".csv"
              className="input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setImportFile(file || null);
                setImportResult(null);
              }}
            />
          </div>

          {importResult && (
            <div
              style={{
                padding: '1rem',
                borderRadius: '4px',
                backgroundColor: importResult.errors?.length > 0 ? 'var(--warning-light)' : 'var(--success-light)',
                border: `1px solid ${importResult.errors?.length > 0 ? 'var(--warning)' : 'var(--success)'}`,
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                Resultado de la importación:
              </h4>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Total:</strong> {importResult.total} piezas
              </p>
              <p style={{ margin: '0.25rem 0', color: 'var(--success)' }}>
                <strong>Creadas:</strong> {importResult.created}
              </p>
              <p style={{ margin: '0.25rem 0', color: 'var(--info)' }}>
                <strong>Actualizadas:</strong> {importResult.updated}
              </p>
              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0.25rem 0', color: 'var(--danger)' }}>
                    <strong>Errores ({importResult.errors.length}):</strong>
                  </p>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                    {importResult.errors.slice(0, 10).map((error: any, idx: number) => (
                      <li key={idx}>
                        Fila {error.row} ({error.cod_pieza}): {error.error}
                      </li>
                    ))}
                    {importResult.errors.length > 10 && (
                      <li style={{ fontStyle: 'italic' }}>
                        ... y {importResult.errors.length - 10} errores más
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

